import { useContext, useEffect, useState } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import { NotificationContext } from '../../../../providers/notification';
import apiCall from '../../../../utils/apiCall';

interface State {
    StateId: number;
    StateName: string;
    CountryId?: number;
    Country: CountryByState;
  }
  
  interface CountryByState {
    CountryId: number;
    CountryName: string;
    Id: number;
    CreatedBy?: any;
    ModifiedBy?: any;
    UserIpAddress?: any;
    CreateDate: string;
    LastModified: string;
    ActiveStatus: boolean;
    IsApprovalRequired?: any;
    RouteId?: any;
    ApprovalTypeId?: any;
    IsDeleted: boolean;
}

interface Payload {
    StateId: number;
    LGAName: string;
}

interface ComponentProps {
    handleClose: () => void;
    data: State[] | undefined;
    isLoading: boolean;
    isError: boolean;
    getStateErrorMessage: string;
}

const CreateLGA: React.FC<ComponentProps> = ({ data, isError, isLoading, getStateErrorMessage, handleClose }) => {
    const { showNotification } = useContext(NotificationContext);
    const queryClient = useQueryClient();

    const postLGA = (data: Payload) => {
        return apiCall().post('/LGA/CreateLGA', data);
    };

    const postLGAMutation = useMutation(['post-state'], postLGA, {retry: 0});

    const postLGAHandler = async (values: FormikValues, resetForm: () => void) => {
        const payload: Payload = {
            StateId: values?.state,
            LGAName: values?.lga,
        }

        try {
            await postLGAMutation.mutateAsync(payload, {
                onSuccess: () => {
                    showNotification({
                        message: 'LGA successfully added',
                        type: 'success',
                    });

                    queryClient.refetchQueries(['get-all-lga']);
                    resetForm();
                    handleClose();
                }
            })
        } catch (error: any){
            showNotification({
                message: error?.response?.data?.title || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        state: Yup.string().required('required'),
        lga: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('required'),
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            state: '',
            lga: ''
        },
        onSubmit: (values, {resetForm}) => {
            postLGAHandler(values, resetForm);
        },
        validationSchema,
    });

    const stateOptions = data && data?.length > 0 ? (
        data?.map((state: State) => (
            <option key={state?.StateId} value={state?.StateId}>
                {state?.StateName}
            </option>
        ))
    ) : (
        <option disabled>
            {isLoading ? "Loading..." : isError ? getStateErrorMessage : ''}
        </option>
    )

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='state'
                        type='text'
                        as={Select}
                        label='State'
                        options={stateOptions}
                    />
                    <Field 
                        name='lga'
                        type='text'
                        as={Input}
                        label='Local Government Area'
                        placeholder='L.G.A'
                    />
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={postLGAMutation.isLoading ? 'Submitting...' : 'Submit'}
                            disabled={postLGAMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default CreateLGA;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`




